
const navigation = {
  home: 'Home',
  sugar: 'Sugar',
  coffee: 'Coffee',
  logistics: 'Logistics',
  news: 'News',
  contact: 'Contact'
};

const hero = {
  title: 'Commodity Exports',
  subtitle: 'Sugar and Coffee from Brazil to the World',
  description: 'Brazil is a global leader in sugar and coffee production and export. This site presents a comprehensive overview of these products, their markets, export logistics and much more.',
  exploreSugar: 'Explore Sugar',
  discoverCoffee: 'Discover Coffee'
};

const features = {
  title: 'Explore Our Sections',
  description: 'Discover detailed information about the processes, markets and logistics of Brazil\'s main commodities',
  sugar: {
    title: 'Learn about the sugar export process',
    description: 'From field to port: understand how Brazilian sugar reaches international markets'
  },
  coffee: {
    title: 'Discover the paths of Brazilian coffee',
    description: 'Tradition and quality that make Brazil the world\'s largest coffee exporter'
  },
  logistics: {
    title: 'Understand logistics and cargo ships',
    description: 'The port and transport infrastructure that enables exports'
  },
  learnMore: 'Learn More'
};

const stats = {
  title: 'Brazil in Numbers',
  description: 'Data demonstrating Brazil\'s importance in the global commodities market',
  sugarExport: 'Of sugar produced is exported',
  coffeeWorld: 'Of world coffee comes from Brazil',
  mainPorts: 'Main export ports',
  countries: 'Destination countries'
};

const cta = {
  title: 'Stay Updated',
  description: 'Follow the latest trends in the commodities market and industry news',
  seeNews: 'See News',
  contact: 'Get in Touch'
};

// Sugar page translations
const sugar = {
  hero: {
    title: 'Brazilian Sugar',
    subtitle: 'From Field to Port'
  },
  types: {
    title: 'Types of Sugar Exported',
    vhp: {
      name: 'VHP (Very High Polarization)',
      description: 'Raw sugar with high polarization, ideal for refining'
    },
    refined: {
      name: 'Refined',
      description: 'White refined sugar, ready for consumption'
    },
    crystal: {
      name: 'Crystal',
      description: 'Crystallized sugar, more common in the domestic market'
    }
  },
  regions: {
    title: 'Main Producing Regions',
    sp: {
      name: 'São Paulo',
      production: '55%',
      description: 'Largest producing region in the country'
    },
    mg: {
      name: 'Minas Gerais',
      production: '12%',
      description: 'Second largest producing region'
    },
    go: {
      name: 'Goiás',
      production: '10%',
      description: 'Important region in the Center-West'
    }
  },
  process: {
    title: 'Export Process',
    step1: {
      title: 'Sugarcane Harvest',
      description: 'Mechanized or manual cane harvesting'
    },
    step2: {
      title: 'Mill Processing',
      description: 'Grinding and extraction of cane juice'
    },
    step3: {
      title: 'Storage and Transport',
      description: 'Storage in silos and road transport'
    },
    step4: {
      title: 'Port Loading',
      description: 'Loading onto bulk carrier ships'
    }
  },
  market: {
    title: 'International Market',
    destinations: 'Main Destinations',
    factors: {
      title: 'Factors Affecting Prices',
      climate: {
        title: 'Weather Conditions:',
        description: 'Droughts or excessive rains affect production'
      },
      oil: {
        title: 'Oil Price:',
        description: 'Influences ethanol production as an alternative'
      },
      demand: {
        title: 'Global Demand:',
        description: 'Population growth and consumption changes'
      },
      exchange: {
        title: 'Exchange Rate:',
        description: 'Dollar variation impacts competitiveness'
      }
    }
  },
  curiosities: {
    title: 'Brazilian Sugar Facts',
    exported: 'Of Brazilian production is exported',
    worldShare: 'Of world sugar comes from Brazil',
    mills: 'Mills operating in the country',
    hectares: 'Hectares of sugarcane planted',
    worldRank: 'Place in world production',
    countries: 'Countries import our sugar'
  }
};

// Coffee page translations
const coffee = {
  hero: {
    title: 'Brazilian Coffee',
    subtitle: 'Tradition and World Quality'
  },
  history: {
    title: 'History and Economic Importance',
    worldProduction: 'World Production',
    jobs: 'Jobs Generated',
    exports: 'Annual Exports',
    importers: 'Importing Countries'
  },
  types: {
    title: 'Types of Coffee Exported',
    arabica: {
      name: 'Arabica',
      percentage: '75%',
      description: 'High quality coffee, smooth and aromatic flavor',
      regions: 'Minas Gerais, São Paulo, Espírito Santo'
    },
    robusta: {
      name: 'Robusta (Conilon)',
      percentage: '25%',
      description: 'More resistant coffee, strong flavor and higher caffeine content',
      regions: 'Espírito Santo, Rondônia, Bahia'
    }
  },
  states: {
    title: 'Producing States',
    mg: {
      name: 'Minas Gerais',
      production: '54%',
      specialty: 'Mountain Arabica'
    },
    es: {
      name: 'Espírito Santo',
      production: '23%',
      specialty: 'Conilon and Arabica'
    },
    sp: {
      name: 'São Paulo',
      production: '9%',
      specialty: 'Traditional Arabica'
    },
    ba: {
      name: 'Bahia',
      production: '8%',
      specialty: 'Conilon and specialty coffees'
    }
  },
  process: {
    title: 'Export Process',
    step1: {
      title: 'Harvest',
      description: 'Manual (stripping) or mechanical'
    },
    step2: {
      title: 'Drying',
      description: 'Natural on patios or artificial'
    },
    step3: {
      title: 'Processing',
      description: 'Hulling and classification'
    },
    step4: {
      title: 'Storage',
      description: 'In bags or containers'
    },
    step5: {
      title: 'Export',
      description: 'Port shipment'
    }
  },
  market: {
    title: 'Destinations and Global Consumption',
    destinations: 'Main Destinations',
    factors: {
      title: 'Market Factors',
      quotes: {
        title: 'International Quotes:',
        description: 'New York and London exchanges determine prices'
      },
      climate: {
        title: 'Weather Conditions:',
        description: 'Frosts and droughts significantly affect prices'
      },
      quality: {
        title: 'Quality:',
        description: 'Specialty coffees achieve premium prices'
      },
      logistics: {
        title: 'Logistics:',
        description: 'Transportation costs influence competitiveness'
      }
    }
  },
  trends: {
    title: 'Market Trends',
    specialty: {
      title: 'Specialty Coffees',
      description: 'Growing demand for single-origin coffees and special brewing methods'
    },
    certifications: {
      title: 'Certifications',
      description: 'Organic, Fair Trade and Rainforest Alliance certifications'
    },
    sustainability: {
      title: 'Sustainability',
      description: 'Environmentally responsible and socially just practices'
    }
  }
};

// Logistics page translations
const logistics = {
  hero: {
    title: 'Logistics and Transport',
    subtitle: 'The Infrastructure Behind Exports'
  },
  ships: {
    title: 'Types of Cargo Ships',
    bulk: {
      name: 'Bulk Carriers',
      description: 'Ships specialized in transporting bulk products like sugar',
      capacity: '60,000 - 200,000 tons'
    },
    containers: {
      name: 'Dry Containers',
      description: 'Transport of coffee in bags and packaged products',
      capacity: '1,000 - 20,000 TEU'
    },
    refrigerated: {
      name: 'Refrigerated Containers',
      description: 'For products requiring temperature control',
      capacity: '200 - 2,000 TEU'
    }
  },
  ports: {
    title: 'Main Export Ports',
    santos: {
      name: 'Port of Santos (SP)',
      description: 'Largest port in Latin America',
      products: 'Sugar, Coffee, Soybeans',
      capacity: '4.8 million TEU/year'
    },
    paranagua: {
      name: 'Port of Paranaguá (PR)',
      description: 'Main export corridor of the South',
      products: 'Sugar, Soybeans, Corn',
      capacity: '1.2 million TEU/year'
    },
    vitoria: {
      name: 'Port of Vitória (ES)',
      description: 'Important for ore and coffee',
      products: 'Coffee, Sugar, Ore',
      capacity: '800 thousand TEU/year'
    },
    itaqui: {
      name: 'Port of Itaqui (MA)',
      description: 'Northeast Gateway',
      products: 'Sugar, Soybeans, Corn',
      capacity: '600 thousand TEU/year'
    }
  },
  storage: {
    title: 'Warehouses and Deposits',
    silos: {
      name: 'Bulk Silos',
      description: 'Vertical storage for bulk sugar',
      capacity: '10,000 - 100,000 tons'
    },
    warehouses: {
      name: 'Customs Warehouses',
      description: 'Warehouses for products awaiting export',
      capacity: '5,000 - 50,000 tons'
    },
    refrigerated: {
      name: 'Refrigerated Warehouses',
      description: 'Conservation of temperature-sensitive products',
      capacity: '1,000 - 20,000 tons'
    }
  },
  transport: {
    title: 'Domestic Transport',
    modes: 'Transport Modes',
    road: {
      name: 'Road',
      percentage: '70%',
      description: 'Main mode for domestic transport'
    },
    rail: {
      name: 'Rail',
      percentage: '25%',
      description: 'Growing use in some regions'
    },
    waterway: {
      name: 'Waterway',
      percentage: '5%',
      description: 'Used in specific regions'
    },
    characteristics: {
      title: 'Transport Characteristics',
      distances: {
        title: 'Average Distances',
        farm: 'Farm → Warehouse: 50-200 km',
        warehouse: 'Warehouse → Port: 200-800 km',
        time: 'Average time: 2-5 days'
      },
      costs: {
        title: 'Logistics Costs',
        percent: 'Represent 15-25% of final price',
        fuel: 'Fuel: largest component',
        tolls: 'Tolls and port fees'
      },
      seasonality: {
        title: 'Seasonality',
        peak: 'Peak during harvest (May-Sep)',
        demand: 'Higher transport demand',
        increase: 'Freight rate increases'
      }
    }
  },
  challenges: {
    title: 'Challenges and Opportunities',
    infrastructure: {
      title: 'Port Infrastructure',
      description: 'Need for modernization and capacity expansion'
    },
    costs: {
      title: 'Logistics Costs',
      description: 'Reduction of transport and storage costs'
    },
    sustainability: {
      title: 'Sustainability',
      description: 'Implementation of environmentally responsible practices'
    },
    digitalization: {
      title: 'Digitalization',
      description: 'Automation and digital process control'
    }
  },
  numbers: {
    title: 'Logistics Numbers',
    tonnage: 'Tons handled/year',
    ports: 'Organized ports',
    terminals: 'Private terminals',
    gdp: 'Logistics cost of GDP'
  }
};

// News page translations
const news = {
  hero: {
    title: 'News and Trends',
    subtitle: 'What\'s Happening in the Export Market?'
  },
  market: {
    title: 'Real-Time Quotes'
  },
  tabs: {
    recent: 'Recent News',
    analysis: 'Analysis',
    forecasts: 'Forecasts'
  },
  analysis: {
    title: 'Market Analysis',
    positive: {
      title: 'Positive Factors',
      climate: 'Favorable climate for next harvest',
      demand: 'Growing international demand',
      infrastructure: 'Improvements in logistics infrastructure'
    },
    challenges: {
      title: 'Challenges',
      exchange: 'Exchange rate volatility',
      competition: 'International competition',
      costs: 'High logistics costs'
    }
  },
  newsletter: {
    title: 'Stay Informed',
    description: 'Receive weekly updates on the commodities market directly in your email',
    placeholder: 'Your email',
    subscribe: 'Subscribe'
  }
};

// Contact page translations
const contact = {
  hero: {
    title: 'About the Project',
    subtitle: 'Meet our team and get in touch with us'
  },
  about: {
    title: 'Who We Are'
  },
  objectives: {
    title: 'Our Objectives',
    educational: {
      title: 'Educational',
      description: 'Educate about production and export processes of Brazil\'s main commodities'
    },
    informative: {
      title: 'Informative',
      description: 'Provide updated data on markets, prices and sector trends'
    },
    connective: {
      title: 'Connective',
      description: 'Connect different audiences interested in Brazilian agribusiness'
    }
  },
  team: {
    title: 'Our Team'
  },
  sources: {
    title: 'Data Sources',
    description: 'We exclusively use official data from recognized institutions to ensure accuracy and reliability of the information presented.'
  },
  form: {
    title: 'Get in Touch',
    subtitle: 'Have questions, suggestions or would like to contribute? Talk to us!',
    name: 'Full Name',
    email: 'Email',
    subject: 'Subject',
    message: 'Message',
    send: 'Send Message',
    sending: 'Sending...',
    success: 'Message sent successfully! We will contact you soon.',
    error: 'Error sending message. Please try again.',
    selectSubject: 'Select a subject',
    subjects: {
      doubt: 'Question about data',
      suggestion: 'Improvement suggestion',
      contribution: 'Data contribution',
      partnership: 'Partnership proposal',
      press: 'Press',
      other: 'Other'
    },
    placeholder: {
      name: 'Your full name',
      email: 'your@email.com',
      message: 'Describe your message (maximum 500 characters)'
    },
    charRemaining: 'characters remaining'
  },
  social: {
    title: 'Follow Us',
    description: 'Follow us on social media for the latest updates on the commodities market'
  }
};

export default {
  navigation,
  hero,
  features,
  stats,
  cta,
  sugar,
  coffee,
  logistics,
  news,
  contact
};
